import { useCallback, useEffect, useState } from 'react';
import { getExercises } from '../../../api/healthAPI';
import { errorToast } from '../../../utils/helper';

const DEFAULT_PAGINATION = {
  page: 1,
  pages: 1,
  total: 0,
  limit: 10
};

const useExercises = ({ type, initialLimit = 10, autoFetch = true } = {}) => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    ...DEFAULT_PAGINATION,
    limit: initialLimit
  });

  const fetchExercises = useCallback(
    async ({ page = 1, limit = pagination.limit, search = searchTerm, showError = true } = {}) => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page,
          limit,
          search,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        };

        if (type) {
          params.type = type;
        }

        const response = await getExercises(params);

        if (response?.meta?.code === 1 && response?.data) {
          setExercises(response.data || []);
          setPagination({
            page: response.meta.page || 1,
            pages: response.meta.pages || 1,
            total: response.meta.total || 0,
            limit: response.meta.limit || limit
          });
          return response.data || [];
        }

        throw new Error(response?.meta?.message || 'Failed to fetch exercises');
      } catch (err) {
        console.error('Error fetching exercises:', err);
        setExercises([]);
        setError(err.message);
        setPagination((prev) => ({
          ...prev,
          page,
          limit
        }));
        if (showError) {
          errorToast('Failed to load exercises');
        }
        return [];
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, searchTerm, type]
  );

  useEffect(() => {
    if (!autoFetch) return;

    const timer = setTimeout(() => {
      fetchExercises({ page: 1, search: searchTerm, showError: false });
    }, 300);

    return () => clearTimeout(timer);
  }, [autoFetch, fetchExercises, searchTerm, type]);

  const goToPage = useCallback(
    (page) => fetchExercises({ page, search: searchTerm }),
    [fetchExercises, searchTerm]
  );

  const refreshExercises = useCallback(() => {
    return fetchExercises({
      page: pagination.page,
      limit: pagination.limit,
      search: searchTerm
    });
  }, [fetchExercises, pagination.limit, pagination.page, searchTerm]);

  return {
    exercises,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    pagination,
    fetchExercises,
    goToPage,
    refreshExercises
  };
};

export default useExercises;
